/**
 * TerrainOrtsnamen — kuratierte Regions-Beschriftung als extrudierte
 * Gold-Lettern (Cinzel), flach auf das Relief gelegt wie die Gravur eines
 * Museums-Kartenmodells.
 *
 * Gold in Echtzeit (Alex 2026-08-16, „guter Pixel-Shader, kein Glow"):
 * Metall lebt ausschließlich von dem, was es SPIEGELT — ohne Environment
 * ist metalness=1 schlicht schwarz. Statt HDR-Asset erzeugen wir einmalig
 * eine prozedurale Studio-Umgebung (RoomEnvironment → PMREM, ~10 ms, kein
 * Netz-Asset) und geben sie NUR diesem Material — die Terrain-Beleuchtung
 * bleibt unberührt. Base Color ist die physikalische Gold-Reflektanz
 * (F0 ≈ 1.0/0.766/0.336 linear); ACES-Tonemapping der Engine gibt den
 * warmen Schimmer gratis dazu.
 *
 * Schrift: typeface.json (Teil-Zeichensatz, map-capture/cinzel-typeface.mjs)
 * → TextGeometry mit Extrusion + Fase. Jeder Name liegt LEVEL auf einer
 * Höhe (getWorldY am Anker + Offset) — Lettern werden nicht einzeln übers
 * Gelände verbogen; die Höhe wird beim Kachel-Streaming nachgeführt.
 */
import * as THREE from 'three';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { SceneNode } from '../core/SceneNode';
import { erzeugeGoldMaterial, gebeGoldMaterialFrei } from './GoldMaterial';
import type { TerrainRoot } from './TerrainRoot';

export interface TerrainOrtsnamenConfig {
    terrain: TerrainRoot;
    /** JSON { orte: [{name, lat, lng, hoeheMeter, drehungGrad?}] } */
    orteUrl: string;
    /** typeface.json (Cinzel-Teilsatz) */
    fontUrl: string;
    /** Liefert den WebGLRenderer fuer die einmalige PMREM-Erzeugung. */
    getRenderer: () => THREE.WebGLRenderer;
    /** Schwebe-Offset ueber dem Gelaende in Metern (Vorgabe 30) */
    hoehenOffsetMeter?: number;
    roughness?: number;
    /** Schmalere Touch-Viewports brauchen kleinere Relief-Lettern. */
    mobileScale?: number;
}

interface Ort {
    name: string;
    lat: number;
    lng: number;
    hoeheMeter: number;
    drehungGrad?: number;
}

interface Label {
    mesh: THREE.Mesh;
    x: number;
    z: number;
    /** halbe Textbreite in Welt-Units (fuers Gelaende-Sampling) */
    halbBreite: number;
    /** Richtung der Textachse in Welt-XZ */
    dirX: number;
    dirZ: number;
}

export class TerrainOrtsnamen extends SceneNode {
    private readonly _cfg: Required<TerrainOrtsnamenConfig>;
    private readonly _terrain: TerrainRoot;
    private _labels: Label[] = [];
    private _drapeUhr = 0;
    private _versuche = 0;
    private _unsubscribeHeights: (() => void) | null = null;
    private _destroyed = false;

    constructor(cfg: TerrainOrtsnamenConfig) {
        super('TerrainOrtsnamen');
        this._cfg = { hoehenOffsetMeter: 30, roughness: 0.26, mobileScale: 0.55, ...cfg };
        this._terrain = cfg.terrain;
        this._mesh = new THREE.Group();
        this._mesh.name = 'TerrainOrtsnamen';
        this._mesh.userData.sceneNode = this;
        this._unsubscribeHeights = this._terrain.onHeightsChanged(() => this.refreshSurface());
        this._lade().catch((e) => console.error('[terrain] Ortsnamen-Aufbau fehlgeschlagen:', e));
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    private async _lade(): Promise<void> {
        const [orteDoc, fontDoc] = await Promise.all([
            fetch(this._cfg.orteUrl).then((r) => r.json()).catch(() => null),
            fetch(this._cfg.fontUrl).then((r) => r.json()).catch(() => null),
        ]);
        const orte: Ort[] = orteDoc?.orte;
        if (this._destroyed) return;
        if (!orte?.length || !fontDoc?.glyphs) {
            console.warn('[terrain] Ortsnamen/Font nicht ladbar:', this._cfg.orteUrl, this._cfg.fontUrl);
            return;
        }
        this._baue(orte, new Font(fontDoc));
    }

    private _baue(orte: Ort[], font: Font): void {
        const proj = this._terrain.projection;
        const material = erzeugeGoldMaterial(this._cfg.getRenderer(), this._cfg.roughness);
        const viewportScale = matchMedia('(pointer: coarse)').matches ? this._cfg.mobileScale : 1;

        for (const ort of orte) {
            const groesse = ort.hoeheMeter * proj.unitsPerMeter * viewportScale;
            const geo = new TextGeometry(ort.name, {
                font,
                size: groesse,
                depth: groesse * 0.22,
                curveSegments: 5,
                bevelEnabled: true,
                bevelThickness: groesse * 0.03,
                bevelSize: groesse * 0.02,
                bevelSegments: 2,
            });
            geo.computeBoundingBox();
            const bb = geo.boundingBox!;
            // zentrieren, dann flach legen: Textflaeche in XZ, Oberkante nach
            // Norden (-Z), Extrusion zeigt nach oben (+Y)
            geo.translate(-(bb.min.x + bb.max.x) / 2, -(bb.min.y + bb.max.y) / 2, 0);
            geo.rotateX(-Math.PI / 2);
            /* Standardansicht der App blickt von Norden auf das Relief —
             * lesbar heisst hier: Oberkante nach Sueden (+Z). */
            geo.rotateY(Math.PI);

            const w = proj.geoToWorld(ort.lng, ort.lat, 0);
            const mesh = new THREE.Mesh(geo, material);
            mesh.name = `ortsname/${ort.name}`;
            mesh.position.set(w.x, 0.5, w.z);
            mesh.visible = false;
            if (ort.drehungGrad) mesh.rotation.y = THREE.MathUtils.degToRad(ort.drehungGrad);
            (this._mesh as THREE.Group).add(mesh);
            const ry = mesh.rotation.y;
            this._labels.push({
                mesh, x: w.x, z: w.z,
                halbBreite: (bb.max.x - bb.min.x) / 2,
                dirX: Math.cos(ry), dirZ: -Math.sin(ry),
            });
        }
        this._drape();
    }

    /** Jeden Namen auf das MAXIMUM des Gelaendes entlang seiner Textbreite
     *  heben (+Offset) — level pro Name, ohne dass Lettern am ansteigenden
     *  Hang im Berg verschwinden. */
    private _drape(): number {
        const offset = this._cfg.hoehenOffsetMeter * this._terrain.projection.unitsPerMeter;
        let summe = 0;
        for (const l of this._labels) {
            /* Gelaende-MAXIMUM entlang der Textbreite — dicht abgetastet.
             * Anlass (Alex 2026-08-17, Screenshot 119757/119758): Mit nur 5
             * Stuetzstellen unterschaetzte ein 30-km-Schriftzug ueber einem
             * zackigen Grat sein Maximum grob — das Gelaende schnitt mitten
             * durch die Lettern ("zerrissener" Text). Jetzt eine Probe je
             * ~200 m (gedeckelt), damit kein Grat mehr durchsticht. */
            const proben = Math.max(9, Math.min(96, Math.round((l.halbBreite * 2) / (200 * this._terrain.projection.unitsPerMeter))));
            let max: number | null = null;
            for (let t = 0; t < proben; t++) {
                const f = (t / (proben - 1) - 0.5) * 2 * l.halbBreite;
                const y = this._terrain.getWorldY(l.x + l.dirX * f, l.z + l.dirZ * f);
                if (y !== null && (max === null || y > max)) max = y;
            }
            if (max !== null) {
                l.mesh.position.y = max + offset;
                summe += max;
            }
        }
        return summe;
    }

    protected onUpdate(deltaTime: number): void {
        if (this._labels.length === 0 || this._versuche >= 12) return;
        this._drapeUhr += deltaTime;
        if (this._drapeUhr < 2) return;
        this._drapeUhr = 0;
        this._drape();
        this._versuche++;
    }

    refreshSurface(): void {
        this._versuche = 0;
        this._drapeUhr = 0;
        this._drape();
    }

    protected override onDestroy(): void {
        this._destroyed = true;
        this._unsubscribeHeights?.();
        this._unsubscribeHeights = null;
        const materials = new Set<THREE.Material>();
        for (const { mesh } of this._labels) {
            mesh.geometry.dispose();
            const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            for (const material of meshMaterials) materials.add(material);
        }
        for (const material of materials) {
            /* EnvMap gehoert dem GoldMaterial-Cache (Refcount) — Embleme
             * anderer Nodes koennen sie weiterverwenden. */
            if (material instanceof THREE.MeshStandardMaterial) gebeGoldMaterialFrei(material);
            else material.dispose();
        }
        this._labels = [];
    }
}
